import { Component, OnInit } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { ComponentBase } from '../../../../../core/_infrastructure/classes/componentBase';
import { NotificationService } from '../../../../../core/_infrastructure/classes/services/notification.service';
import { ExceptionManager } from '../../../../../core/_infrastructure/classes/handlers/exceptionManager';
import { AuthService } from '../../../../../core/_infrastructure/classes/services/auth.service';
import { NavMenuService } from '../../../../../core/components/navmenu/navmenu.service';
import { GlobalService } from '../../../../../core/_infrastructure/classes/services/global.service';
import { Tc } from '../../../../../core/_infrastructure/types/common';
import { PeriodoInspeccionService } from '../inspecciones.service';
import { InspeccionPeriodoCamViewModel } from '../inspecciones.types';
import { DatosPeriodoViewModel } from '../../plazos/plazos.types';
import { PeriodoPlazoService } from '../../plazos/plazos.service';
import { PeriodoCamService } from '../../datosbasicos/periodocam.service';
import { PeriodoCamViewModel } from '../../datosbasicos/periodocam.types';
import { EnumTcTipoInspeccionCam } from 'src/app/components/_campanias/unidades-inspeccion/inspeccion.types';

@Component({
    selector: 'app-inspecciones-wrapper-edit',
    templateUrl: './inspecciones-wrapper-edit.component.html',
})
export class InspeccionesWrapperEditComponent extends ComponentBase implements OnInit {
    /**
     * @article: Reactive forms with smart dumb components
     * https://medium.com/angular-athens/reactive-forms-with-smart-dumb-components-48ebc81417c7
     */

    visibilitySpinner = false;
    inspeccionPeriodo: InspeccionPeriodoCamViewModel;
    datosPeriodo: DatosPeriodoViewModel;
    IdPeriodo: any;
    tiposInspeccion: Tc[];

    successMessage = 'Inspección guardada correctamente';
    listUrl = '/mantenimiento/periodos/inspecciones/';
    esVigenteConSolicitudes = false;
    esObligatoriaPrimeraInspeccion = false; // Existe un plazo configurado para el periodo que obliga a configurar una primera inspección obligatoria.
    periodoCam: PeriodoCamViewModel;

    activatedRoute$ = this.activatedRoute.paramMap.subscribe(params => {
        const IdPeriodo = params.get('id');
        if (IdPeriodo) {
            this.IdPeriodo = parseInt(IdPeriodo);
        }

        const id: any = params.get('idinspeccion');
        if (id) {
            // Llega por /inspecciones/:id/edit/:idinspeccion
            this.getInspeccionPeriodo(id);
        } else {
            // Llega por /inspecciones/id:/create
            this.getDataByIdPeriodo(this.IdPeriodo);
        }
    });

    constructor(
        private fb: FormBuilder,
        private activatedRoute: ActivatedRoute,
        private router: Router,
        private periodoCamService: PeriodoCamService,
        private inspeccionesService: PeriodoInspeccionService,
        protected notificationService: NotificationService,
        protected authService: AuthService,
        private globalService: GlobalService,
        private plazosService: PeriodoPlazoService,
        protected exceptionHandler: ExceptionManager,
        private navMenuService: NavMenuService,
    ) {
        super(authService, notificationService, exceptionHandler);
    }

    ngOnInit(): void {
        this.preparePresentation();
    }

    private preparePresentation(): void {
        this.getTiposInspeccion();
        this.buildForm();
    }

    private buildForm(): void {
        this.form = this.fb.group({
            Id: [0],
            IdPeriodo: [0],
            IdTipoInspeccion: [0],
            Nombre: [''],
            Definicion: [''],
            TieneFechaFin: [false],
            Obligatoria: [false],
        });
    }

    private getTiposInspeccion(): void {
        this.globalService.getTiposInspeccionCam().subscribe((tiposInspeccion: Tc[]) => {
            this.tiposInspeccion = tiposInspeccion;
        });
    }

    /**
     * Método que invoca a la API para cargar los datos de una inspección a partir de su id.
     * @param idInspeccion number
     * @return void
     */
    private getInspeccionPeriodo(idInspeccion: number): void {
        this.inspeccionesService
            .getInspeccionPeriodo(idInspeccion)
            .pipe(
                tap((i: InspeccionPeriodoCamViewModel) => {
                    this.inspeccionPeriodo = i;
                    this.inspeccionesService.inspeccionPeriodo = this.inspeccionPeriodo;
                    this.getDataByIdPeriodo(this.inspeccionPeriodo?.IdPeriodo);
                }),
                catchError(error => {
                    this.catchServerError(error);
                    this.form.disable();
                    return of(null);
                }),
            )
            .subscribe();
    }

    private getDataByIdPeriodo(idPeriodo: number): void {
        this.getDatosPlazosPeriodo(idPeriodo);
        this.getSiVigenteConSolicitudes(idPeriodo);
        this.getEsObligatoriaPrimeraInspeccion(this.IdPeriodo);
    }

    private getDatosPlazosPeriodo(idPeriodo: number): void {
        this.plazosService
            .getDatosPeriodo(idPeriodo)
            .pipe(
                tap((d: DatosPeriodoViewModel) => {
                    this.datosPeriodo = d;
                    this.navMenuService.updateMenuServiceForPeriodo(d.IdPeriodo!);
                }),
                catchError(error => {
                    this.catchServerError(error);
                    this.form.disable();
                    return of(null);
                }),
            )
            .subscribe();
    }

    /**
     * Será obligatoria la primera inspección si así está configurado en algún plazo del periodo y todavía no se ha configurado ninguna inspección de tipo primera inspección
     * @param idPeriodo number
     */
    private getEsObligatoriaPrimeraInspeccion(idPeriodo: number): void {
        this.plazosService
            .getEsObligatoriaPrimeraInspeccion(idPeriodo)
            .pipe(
                tap((res: boolean) => {
                    this.esObligatoriaPrimeraInspeccion = res;
                }),
                catchError(error => {
                    this.catchServerError(error);
                    return of(null);
                }),
            )
            .subscribe();
    }

    /**
     * Método que invoca a la API para comprueba si el periodo es vigente y tiene solicitudes de campaña asociadas.
     * @param idPeriodo number
     * @return void
     */
    private getSiVigenteConSolicitudes(idPeriodo: number): void {
        this.periodoCamService
            .getPeriodoEsVigente(idPeriodo)
            .pipe(
                tap((p: boolean) => {
                    this.esVigenteConSolicitudes = p;
                    if (this.esVigenteConSolicitudes) {
                        this.form.disable();
                    }
                }),
                catchError(error => {
                    this.catchServerError(error);
                    return of(null);
                }),
            )
            .subscribe();
    }

    /**
     * Invocación de la API para salvar los cambios realizados en el periodo
     * @return void
     */
    onSave(): void {
        const model = this.form.getRawValue();
        model.IdPeriodo = this.IdPeriodo;

        if (!this.form.valid) {
            this.addErrorMessage('Revise los datos de la Inspección antes de guardar');
            return;
        }
        const payload = model as InspeccionPeriodoCamViewModel;
        if (this.esObligatoriaPrimeraInspeccion) {
            if (payload.IdTipoInspeccion !== EnumTcTipoInspeccionCam.ID_PRIMERA_INSPECCION) {
                this.addErrorMessage('Es obligatorio que el periodo tenga configurada al menos una inspección de tipo primera inspección');
                return;
            }
        }

        this.visibilitySpinner = true;

        this.inspeccionesService
            .save(payload)
            .pipe(
                tap(() => {
                    this.visibilitySpinner = false;
                    this.addSuccessMessage(this.successMessage);
                    this.onBack();
                }),
                catchError(error => {
                    this.visibilitySpinner = false;
                    this.catchServerError(error);
                    return of(null);
                }),
            )
            .subscribe();
    }

    onBack(): void {
        this.router.navigate([this.listUrl + this.IdPeriodo]);
    }

    get formInvalid(): boolean {
        return !this.form.valid || !this.form.dirty || this.form.pristine;
    }

    get esPeriodoFinalizado(): boolean {
        const hoy = new Date();
        let fechaFinPeriodo;
        if (this.inspeccionPeriodo?.PeriodoFechaFin) {
            fechaFinPeriodo = new Date(this.inspeccionPeriodo.PeriodoFechaFin);
            return hoy >= fechaFinPeriodo;
        }
        if (this.datosPeriodo?.FechaFinPeriodo) {
            fechaFinPeriodo = new Date(this.datosPeriodo.FechaFinPeriodo);
            return hoy >= fechaFinPeriodo;
        }
        fechaFinPeriodo = new Date(this.periodoCam?.FechaFin);
        return hoy >= fechaFinPeriodo;
    }
}
